import RootStore from '../../root-store';
import { validateMaterialSubstitution } from './validation';
import {
  stopOriginalBin,
  handleDeferredWeight,
} from './bin-control';
import {
  updateInventoryIfEnabled,
  createSubstituteWeighedData,
} from './data-management';
import { updateDeviceStatuses } from './device-status';
import {
  logSubstitution,
  resumeBatchWithSubstitute,
} from './logging';
import { ChangeMaterialMidBatchParams } from './types';
import {
  findExistingSubstituteMaterial,
  combineSubstituteWeights,
  updateExistingSubstituteEntry,
  logCombinedSubstitution,
  createNewSubstituteEntry,
  logSubstitutionCompletion,
} from './substitute-material-handling';
import { updateMixDesignForSubstitution } from './mix-design-update';

/**
 * Changes material mid-batch by substituting one material with another while preserving batch integrity.
 * This function handles the complex process of switching materials during an active batch operation,
 * ensuring all systems, weights, and data remain consistent.
 */
export const changeMaterialMidBatch = (rootStore: RootStore, params: ChangeMaterialMidBatchParams) => {
  const { batchStore } = rootStore;
  const { reason = 'Material substitution', currentWeight = 0 } = params;

  // STEP 1: Validate the substitution request
  // Ensures materials are compatible, bins are available, and batch is in correct state
  const validation = validateMaterialSubstitution(rootStore, params);
  if (!validation.isValid) {
    batchStore.showError(validation.error);
    return false;
  }

  // STEP 2: Stop the original bin safely
  // Closes gates, stops devices, and clears timers to prevent continued feeding
  stopOriginalBin(batchStore, validation.originalBin, validation.originalMaterial, validation.scale);

  // STEP 3: Capture current state and handle deferred weights
  // Records current scale weight and transfers any pending weight data to the new bin
  const currentScaleWeight = batchStore.getScaleWeight(validation.scale);
  const deferredWeight = handleDeferredWeight(batchStore, params.scaleId, validation.originalBin, validation.substituteBin);

  // STEP 4: Update inventory tracking (if enabled)
  // Records the amount of original material used before substitution for inventory management
  updateInventoryIfEnabled(
    rootStore.materialStore,
    batchStore,
    validation.originalMaterial,
    validation.substituteMaterial,
    currentWeight,
    validation.scale,
    validation.runningData,
    validation.originalBin,
  );

  // STEP 5: Mark the original scale data as finished and interrupted
  // Flags indicate this material feeding was stopped mid-process due to substitution
  validation.scaleData.isFeedFinish = true;
  validation.scaleData.isFeedInterrupted = true;

  // STEP 6: Create new weighed data for the substitute material
  // Builds a complete data structure for the new material with substitution metadata
  const newWeighedData = createSubstituteWeighedData(
    validation.scaleData,
    validation.runningData,
    validation.substituteMaterial,
    validation.substituteMaterialDetails,
    validation.substituteBin,
    validation.originalMaterial,
    validation.originalBin,
    reason,
    currentWeight,
    currentScaleWeight,
    batchStore,
  );

  // STEP 7: Update mixDesign to reflect the actual weights for both materials
  updateMixDesignForSubstitution(
    validation.runningData,
    validation.originalMaterial,
    validation.substituteMaterial,
    validation.scaleData,
    currentWeight,
  );

  // STEP 8: Check if substitute material already exists in scale data and combine or replace accordingly
  const existingSubstituteIndex = findExistingSubstituteMaterial(
    validation.runningData,
    params.scaleId,
    validation.substituteMaterial.itemId,
  );

  let activeSubstituteScaleData: any;
  let isCombinedWithExisting = false;

  if (existingSubstituteIndex !== -1) {
    // STEP 8A: Substitute material already exists - combine weights and update existing entry
    const existingSubstituteData = validation.runningData.scales[params.scaleId][existingSubstituteIndex];
    activeSubstituteScaleData = existingSubstituteData;
    isCombinedWithExisting = true;

    combineSubstituteWeights(existingSubstituteData, currentWeight, newWeighedData);

    updateExistingSubstituteEntry(existingSubstituteData, validation.substituteBin, validation.scaleData);

    logCombinedSubstitution(
      batchStore,
      validation.substituteMaterial,
      currentWeight,
      existingSubstituteData,
      params.scaleId,
    );
  } else {
    // STEP 8B: No existing substitute material - create new entry
    activeSubstituteScaleData = createNewSubstituteEntry(
      validation.runningData,
      params.scaleId,
      validation.scaleDataIndex,
      newWeighedData,
    );
    isCombinedWithExisting = false;
  }

  // STEP 9: Find the constituent data for device status updates
  // Needed to properly update device displays with correct material information
  const constituent = validation.scaleData.constituent || validation.runningData.mixDesign?.find((c: any) =>
    (c.batchCode && validation.originalMaterial.batchCode && c.batchCode === validation.originalMaterial.batchCode) ||
    c.constituentItemId === validation.originalMaterial.itemId,
  );

  // STEP 10: Update all device statuses
  // Changes device displays, holds the new bin, and updates weight tracking
  updateDeviceStatuses(
    batchStore,
    rootStore.materialStore,
    validation.originalBin,
    validation.substituteBin,
    params.scaleId,
    validation.substituteMaterial,
    currentWeight,
    currentScaleWeight,
    deferredWeight,
    activeSubstituteScaleData,
    constituent,
  );

  // STEP 11: Log the substitution for audit trail
  // Records the change with all relevant details for troubleshooting and compliance
  logSubstitution(
    batchStore,
    validation.originalMaterial,
    validation.substituteMaterial,
    validation.originalBin,
    validation.substituteBin,
    params,
    currentScaleWeight,
    deferredWeight,
    validation.runningData,
    activeSubstituteScaleData,
  );

  // Additional logging for combined vs new substitution scenarios
  logSubstitutionCompletion(
    batchStore,
    isCombinedWithExisting,
    validation.originalMaterial,
    validation.substituteMaterial,
    params.scaleId,
    activeSubstituteScaleData,
  );

  // STEP 12: Resume batch operation with the new material
  // Automatically continues the batch process using the substitute material
  resumeBatchWithSubstitute(batchStore, params.scaleId, validation.runningData, validation.substituteMaterial);

  return true;
};
